import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, Check, X, Circle } from "lucide-react";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("fr-BE", {
    dateStyle: "short",
    timeStyle: "medium",
  });

const statusBadge = (status) => {
  if (status >= 200 && status < 300) {
    return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
  }
  if (status === 401 || status === 403) {
    return "border-amber-500/40 bg-amber-500/10 text-amber-300";
  }
  if (status === 429) {
    return "border-orange-500/40 bg-orange-500/10 text-orange-300";
  }
  if (status >= 400 && status < 500) {
    return "border-yellow-500/40 bg-yellow-500/10 text-yellow-300";
  }
  return "border-red-500/40 bg-red-500/10 text-red-300";
};

export default function IngestLogPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/webhooks/ingest-log?limit=50");
      setEntries(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6" data-testid="ingest-log-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Journal debug — Webhook ingest</h1>
          <p className="text-sm text-slate-400 max-w-2xl">
            50 dernières tentatives d&apos;ingestion (tous statuts confondus).
            Utile pour diagnostiquer une landing qui ne remonte pas de lead.
            Conservation 30 jours.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
          data-testid="ingest-log-refresh"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="overflow-x-auto p-0">
          {loading ? (
            <div className="p-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">
              <p>Aucune tentative d&apos;ingestion enregistrée pour l&apos;instant.</p>
              <p className="mt-2 text-xs">
                Dès qu&apos;une requête arrive sur{" "}
                <code className="rounded bg-slate-950 px-1 text-slate-300">
                  /api/ingest/*
                </code>
                {" "}(bon token ou non), elle apparaît ici en moins d&apos;une seconde.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="ingest-log-table">
              <thead className="border-b border-slate-800 text-slate-400">
                <tr>
                  <th className="p-3 text-left">Timestamp</th>
                  <th className="p-3 text-left">IP</th>
                  <th className="p-3 text-left">Path</th>
                  <th className="p-3 text-left">Token</th>
                  <th className="p-3 text-center">Valide</th>
                  <th className="p-3 text-center">HTTP</th>
                  <th className="p-3 text-left">Body keys</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.id}
                    className="border-b border-slate-800/60 last:border-0 hover:bg-slate-800/30"
                    data-testid={`ingest-log-row-${e.id}`}
                  >
                    <td className="p-3 font-mono text-xs text-slate-400 whitespace-nowrap">
                      {formatDate(e.timestamp)}
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-500">{e.ip || "—"}</td>
                    <td className="p-3 max-w-[220px] truncate font-mono text-xs text-slate-300" title={e.path}>
                      {e.path}
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-400">
                      {e.token_prefix ? `${e.token_prefix}…` : "—"}
                    </td>
                    <td className="p-3 text-center">
                      {e.token_valid ? (
                        <Check className="mx-auto h-4 w-4 text-emerald-400" />
                      ) : (
                        <X className="mx-auto h-4 w-4 text-red-400" />
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={`border ${statusBadge(e.response_status)}`}>
                        {e.response_status}
                      </Badge>
                    </td>
                    <td className="p-3 max-w-[240px] truncate text-xs text-slate-400">
                      {e.body_keys?.length ? e.body_keys.join(", ") : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDetail(e)}
                        className="border-slate-700 bg-slate-800 text-xs text-slate-200 hover:bg-slate-700"
                        data-testid={`ingest-log-detail-${e.id}`}
                      >
                        Détails
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent
          className="max-w-3xl border-slate-800 bg-slate-900 text-slate-100"
          data-testid="ingest-log-detail-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Circle
                className={`h-3 w-3 ${
                  detail?.token_valid ? "fill-emerald-400 text-emerald-400" : "fill-red-400 text-red-400"
                }`}
              />
              Tentative — {detail && formatDate(detail.timestamp)}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 rounded border border-slate-700 bg-slate-950/60 p-3 text-xs">
                <div>
                  <span className="text-slate-500">Path :</span>{" "}
                  <code className="text-slate-200">{detail.path}</code>
                </div>
                <div>
                  <span className="text-slate-500">Method :</span> {detail.method}
                </div>
                <div>
                  <span className="text-slate-500">IP :</span>{" "}
                  <code className="text-slate-200">{detail.ip || "—"}</code>
                </div>
                <div>
                  <span className="text-slate-500">Token prefix :</span>{" "}
                  <code className="text-slate-200">{detail.token_prefix || "—"}</code>
                </div>
                <div>
                  <span className="text-slate-500">Token valide :</span>{" "}
                  {detail.token_valid ? "oui" : "non"}
                </div>
                <div>
                  <span className="text-slate-500">HTTP status :</span>{" "}
                  <Badge className={`border ${statusBadge(detail.response_status)}`}>
                    {detail.response_status}
                  </Badge>
                </div>
                {detail.matched_company_id && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Company match :</span>{" "}
                    <code className="text-slate-200">{detail.matched_company_id}</code>
                  </div>
                )}
                {detail.error && (
                  <div className="col-span-2 text-red-300">
                    <span className="text-slate-500">Erreur :</span> {detail.error}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">
                  Headers (filtrés)
                </div>
                <pre
                  className="max-h-40 overflow-auto rounded border border-slate-700 bg-slate-950 p-2 text-[11px] text-slate-300"
                  data-testid="ingest-log-headers"
                >
                  {JSON.stringify(detail.headers || {}, null, 2)}
                </pre>
              </div>

              <div>
                <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">
                  Body reçu (tronqué à 2000 chars)
                </div>
                <pre
                  className="max-h-48 overflow-auto rounded border border-slate-700 bg-slate-950 p-2 text-[11px] text-slate-300 whitespace-pre-wrap"
                  data-testid="ingest-log-body"
                >
                  {detail.body_raw || "(vide)"}
                </pre>
                {detail.body_keys?.length > 0 && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    Clés détectées : {detail.body_keys.join(", ")}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">
                  Réponse envoyée (tronquée à 500 chars)
                </div>
                <pre className="max-h-32 overflow-auto rounded border border-slate-700 bg-slate-950 p-2 text-[11px] text-slate-300 whitespace-pre-wrap">
                  {detail.response_body || "(vide)"}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
